import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  JourneyTaskProgressRow,
  JourneyTaskProofPayload,
} from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PortalJourneyDetail } from "./types";

// POST /api/portal/journeys/progress/:progressId/proof — record the
// discriminated proof_payload, optionally flipping status to completed in
// the same call. The 3-key invalidation matches use-set-progress-status:
//   1. portal.journeys.detail(assignmentId)
//   2. portal.tasks.list()
//   3. portal.tasks.openCount()

type Input = {
  progressId: string;
  assignmentId: string;
  payload: JourneyTaskProofPayload;
  completeOnRecord?: boolean;
};

type Context = {
  previousDetail: PortalJourneyDetail | undefined;
};

export function useRecordProofPayload() {
  const queryClient = useQueryClient();
  return useMutation<
    JourneyTaskProgressRow,
    ApiClientError,
    Input,
    Context
  >({
    mutationFn: ({ progressId, payload, completeOnRecord }) =>
      apiFetch<JourneyTaskProgressRow>(
        `/portal/journeys/progress/${progressId}/proof`,
        {
          method: "POST",
          body: JSON.stringify({
            payload,
            complete_on_record: completeOnRecord ?? true,
          }),
        },
      ),
    onMutate: async ({ progressId, assignmentId, payload, completeOnRecord }) => {
      const key = queryKeys.portal.journeys.detail(assignmentId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousDetail =
        queryClient.getQueryData<PortalJourneyDetail>(key);
      queryClient.setQueryData<PortalJourneyDetail>(key, (old) =>
        old
          ? {
              ...old,
              progress: old.progress.map((p) =>
                p.id === progressId
                  ? {
                      ...p,
                      proof_payload: payload,
                      ...(completeOnRecord !== false
                        ? {
                            status: "completed" as const,
                            completed_at: new Date().toISOString(),
                            snoozed_until: null,
                            snooze_reason: null,
                          }
                        : {}),
                    }
                  : p,
              ),
            }
          : old,
      );
      return { previousDetail };
    },
    onError: (err, { assignmentId }, ctx) => {
      if (ctx?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.portal.journeys.detail(assignmentId),
          ctx.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to record proof");
    },
    onSettled: (_data, _err, { assignmentId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.journeys.detail(assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.tasks.list(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.tasks.openCount(),
      });
    },
  });
}
